import { useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useCampusContext } from "@/contexts/CampusContext";
import useViewMode from "@/hooks/useViewMode";
import { queryKeys } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Tag, AlertTriangle, Search, ChevronLeft, ChevronRight, MapPin, Copy, LayoutGrid, List } from "lucide-react";
import { z } from "zod";

const categorySchema = z.object({
  name: z.string().min(2, "Category name must be at least 2 characters").max(100, "Category name must be under 100 characters"),
  description: z.string().max(500, "Description must be under 500 characters").optional().or(z.literal("")),
});

export default function Categories() {
  const { user } = useAuth();
  const { selectedOrg, isSuperadmin } = useOrganizationContext();
  const { selectedCampus, setSelectedCampus, campuses, isOrgAdmin, isMultiCampusAdmin } = useCampusContext();
  const canManageCategories = isSuperadmin || user?.permissions?.can_manage_categories === true;
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });

  // Delete warning dialog
  const [deleteWarningDialog, setDeleteWarningDialog] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [categoryUsage, setCategoryUsage] = useState({ question_count: 0, is_used: false });

  // Copy to campus dialog
  const [copyCategory, setCopyCategory] = useState(null);
  const [copyCampusOpen, setCopyCampusOpen] = useState(false);
  const [copyTargetCampusId, setCopyTargetCampusId] = useState("");
  const [copyingCategory, setCopyingCategory] = useState(false);

  const [formErrors, setFormErrors] = useState({});

  // Pagination state
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useViewMode("categories"); // "card" | "list"

  const orgId = selectedOrg?.id;

  // Fetch categories list
  const { data: categoriesData, isLoading: loading } = useQuery({
    queryKey: queryKeys.categories.list(page, searchTerm, orgId, selectedCampus?.id),
    queryFn: async () => {
      const params = { page, limit: 50 };
      if (searchTerm) params.search = searchTerm;
      if (isSuperadmin && orgId) {
        params.organization_id = orgId;
      }
      if ((isOrgAdmin || isMultiCampusAdmin) && selectedCampus?.id) {
        params.campus_id = selectedCampus.id;
      }
      const res = await API.get("/categories", { params });
      return res.data;
    },
    enabled: !isSuperadmin || !!selectedOrg,
    placeholderData: keepPreviousData,
  });

  const categories = categoriesData?.categories || [];
  const pagination = categoriesData?.pagination || { total: 0, pages: 0, limit: 50 };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "" });
    setDialog(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, description: cat.description || "" });
    setDialog(true);
  };

  // Save (create/update) mutation
  const saveMutation = useMutation({
    mutationFn: ({ isEdit, id, payload }) => {
      if (isEdit) {
        return API.put(`/categories/${id}`, payload);
      }
      return API.post("/categories", payload);
    },
    onSuccess: (_, { isEdit }) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success(isEdit ? "Category updated" : "Category created");
      setDialog(false);
    },
    onError: () => toast.error("Failed to save category"),
  });

  const handleSave = () => {
    const result = categorySchema.safeParse(form);
    if (!result.success) {
      const errors = {};
      result.error.issues.forEach((issue) => {
        errors[issue.path[0]] = issue.message;
      });
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    const payload = { ...form };
    if (!editing && isSuperadmin && orgId) {
      payload.organization_id = orgId;
    }
    saveMutation.mutate({ isEdit: !!editing, id: editing?.id, payload });
  };

  const openDeleteWarning = async (category) => {
    try {
      const res = await API.get(`/categories/${category.id}/usage`);
      setCategoryUsage(res.data);
      setDeletingCategory(category);
      setDeleteWarningDialog(true);
    } catch {
      toast.error("Failed to check category usage");
    }
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => API.delete(`/categories/${id}`),
    onSuccess: (res) => {
      if (res.data.questions_unattached > 0) {
        toast.success(`Category deleted and unattached from ${res.data.questions_unattached} question(s)`);
      } else {
        toast.success("Category deleted");
      }
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setDeleteWarningDialog(false);
      setDeletingCategory(null);
    },
    onError: () => toast.error("Failed to delete category"),
  });

  const confirmDelete = () => {
    if (!deletingCategory) return;
    deleteMutation.mutate(deletingCategory.id);
  };

  const openCopyToCampus = (cat) => {
    setCopyCategory(cat);
    setCopyTargetCampusId("");
    setCopyCampusOpen(true);
  };

  const confirmCopyToCampus = async () => {
    if (!copyTargetCampusId || !copyCategory) return;
    setCopyingCategory(true);
    try {
      await API.post(`/categories/${copyCategory.id}/copy?target_campus_id=${copyTargetCampusId}`);
      const targetName = campuses.find(c => c.id === copyTargetCampusId)?.name;
      toast.success(`Category copied to ${targetName}`);
      setCopyCampusOpen(false);
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    } catch {
      toast.error("Failed to copy category");
    } finally {
      setCopyingCategory(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <style>{`@keyframes cat-shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{background:'white',border:'1px solid #e2e8f0',borderRadius:10,padding:'16px 20px',display:'flex',alignItems:'center',gap:14,opacity:1-i*0.1}}>
            <div style={{width:36,height:36,borderRadius:8,flexShrink:0,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'cat-shimmer 1.4s ease infinite'}} />
            <div style={{flex:1,display:'flex',flexDirection:'column',gap:8}}>
              <div style={{height:13,width:`${130+i*20}px`,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'cat-shimmer 1.4s ease infinite'}} />
              <div style={{height:10,width:80,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'cat-shimmer 1.4s ease infinite'}} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <style>{`

        .refined-root {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .refined-header {
          font-family: 'Lexend', sans-serif;
          font-weight: 600;
          letter-spacing: -0.025em;
        }

        .refined-category-card {
          background: white;
          border: 1px solid rgb(226 232 240);
          border-radius: 10px;
          padding: 18px;
          transition: all 0.2s ease;
        }

        .refined-category-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px 0 rgb(0 0 0 / 0.06);
          border-color: rgb(203 213 225);
        }

        .refined-search-input {
          border: 1px solid rgb(226 232 240);
          border-radius: 8px;
          padding-left: 40px;
          height: 42px;
          transition: all 0.2s ease;
        }

        .refined-search-input:focus {
          border-color: rgb(99 102 241);
          box-shadow: 0 0 0 3px rgb(99 102 241 / 0.1);
          outline: none;
        }

        .refined-action-button {
          transition: all 0.2s ease;
        }

        .refined-action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px 0 rgb(0 0 0 / 0.1);
        }

        .refined-icon-button {
          padding: 6px;
          border-radius: 6px;
          transition: all 0.15s ease;
        }

        .refined-icon-button:hover {
          transform: scale(1.1);
        }

        .refined-dialog-header {
          font-family: 'Lexend', sans-serif;
          font-size: 18px;
          font-weight: 600;
          letter-spacing: -0.02em;
          color: rgb(15 23 42);
        }

        .refined-label {
          font-family: 'Lexend', sans-serif;
          font-size: 13px;
          font-weight: 500;
          color: rgb(51 65 85);
        }

        .refined-input {
          border: 1px solid rgb(226 232 240);
          border-radius: 6px;
          height: 40px;
          transition: all 0.2s ease;
        }

        .refined-input:focus {
          border-color: rgb(99 102 241);
          box-shadow: 0 0 0 3px rgb(99 102 241 / 0.1);
          outline: none;
        }

        .refined-pagination {
          background: linear-gradient(to right, rgb(248 250 252), white, rgb(248 250 252));
          border: 1px solid rgb(226 232 240);
          border-radius: 10px;
          padding: 16px 20px;
          margin-top: 24px;
        }

        .refined-empty-state {
          background: linear-gradient(to bottom, white, rgb(248 250 252));
          border: 1px solid rgb(226 232 240);
          border-radius: 12px;
          padding: 64px 24px;
          text-align: center;
        }

        .refined-alert-warning {
          background: rgb(254 252 232);
          border: 1.5px solid rgb(253 230 138);
          border-radius: 8px;
          padding: 14px 16px;
        }

        .refined-alert-action {
          background: linear-gradient(135deg, rgb(220 38 38), rgb(185 28 28));
          transition: all 0.2s ease;
        }

        .refined-alert-action:hover {
          box-shadow: 0 4px 12px 0 rgb(220 38 38 / 0.3);
        }

        .refined-view-toggle {
          display: flex;
          align-items: center;
          border: 1px solid rgb(226 232 240);
          border-radius: 8px;
          overflow: hidden;
          flex-shrink: 0;
        }

        .refined-view-toggle-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 0 12px;
          height: 42px;
          font-size: 13px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
          border: none;
          outline: none;
        }

        .refined-view-toggle-btn.active {
          background: rgb(15 23 42);
          color: white;
        }

        .refined-view-toggle-btn:not(.active) {
          background: white;
          color: rgb(100 116 139);
        }

        .refined-view-toggle-btn:not(.active):hover {
          background: rgb(248 250 252);
          color: rgb(51 65 85);
        }

        .refined-list-table {
          border: 1px solid rgb(226 232 240);
          border-radius: 10px;
          overflow: hidden;
        }

        .refined-list-header {
          display: grid;
          grid-template-columns: 1fr 1.6fr auto;
          gap: 16px;
          padding: 10px 18px;
          background: rgb(248 250 252);
          border-bottom: 1px solid rgb(226 232 240);
          font-family: 'DM Sans', sans-serif;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: rgb(100 116 139);
        }

        .refined-list-row {
          display: grid;
          grid-template-columns: 1fr 1.6fr auto;
          gap: 16px;
          align-items: center;
          padding: 12px 18px;
          background: white;
          transition: background 0.15s ease;
        }

        .refined-list-header.with-campus,
        .refined-list-row.with-campus {
          grid-template-columns: 1fr 1.4fr 0.8fr auto;
        }

        .refined-list-row:not(:last-child) {
          border-bottom: 1px solid rgb(241 245 249);
        }

        .refined-list-row:hover {
          background: rgb(248 250 252);
        }

        .refined-list-footer {
          padding: 8px 18px;
          background: rgb(248 250 252);
          border-top: 1px solid rgb(241 245 249);
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: rgb(148 163 184);
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .fade-in {
          animation: fadeIn 0.4s ease;
        }

        .fade-in-delay {
          animation: fadeIn 0.4s ease 0.1s backwards;
        }

        .fade-in-delay-2 {
          animation: fadeIn 0.4s ease 0.2s backwards;
        }
      `}</style>

      <div className="refined-root space-y-6" data-testid="categories-page">
        {/* Header */}
        <div className="flex items-center justify-between fade-in">
          <div>
            <h1 className="refined-header text-3xl text-slate-900 flex items-center gap-3">
              <div className="p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                <Tag className="h-6 w-6 text-emerald-700" />
              </div>
              Categories
            </h1>
            <p className="text-sm text-slate-600 mt-2 ml-14">
              Tag questions for category-wise scoring
            </p>
          </div>
          {canManageCategories && (
            <Button
              onClick={openCreate}
              data-testid="create-category-btn"
              className="refined-action-button bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> New Category
            </Button>
          )}
        </div>

        {/* Search + Campus Filter + View Toggle */}
        <div className="flex gap-3 fade-in-delay">
          <div className="relative flex-1">
            <Input
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="refined-search-input"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          </div>
          {isOrgAdmin && campuses.length > 0 && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <Select
                value={selectedCampus?.id || "all"}
                onValueChange={(val) => {
                  setSelectedCampus(val === "all" ? null : campuses.find(c => c.id === val));
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-44 h-10 border-slate-200 text-sm">
                  <SelectValue placeholder="All Campuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Campuses</SelectItem>
                  {campuses.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* View toggle */}
          <div className="refined-view-toggle">
            <button
              onClick={() => setViewMode("card")}
              className={`refined-view-toggle-btn ${viewMode === "card" ? "active" : ""}`}
              title="Card view"
            >
              <LayoutGrid style={{ width: 15, height: 15 }} />
              <span className="hidden sm:inline">Cards</span>
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`refined-view-toggle-btn ${viewMode === "list" ? "active" : ""}`}
              title="List view"
            >
              <List style={{ width: 15, height: 15 }} />
              <span className="hidden sm:inline">List</span>
            </button>
          </div>
        </div>

        {/* Categories List */}
        {categories.length === 0 ? (
          <div className="refined-empty-state fade-in-delay-2">
            <svg width="88" height="76" viewBox="0 0 88 76" fill="none" className="mx-auto mb-5 opacity-80">
              <rect x="10" y="16" width="50" height="44" rx="8" fill="#EEF2FF" stroke="#C7D2FE" strokeWidth="1.5"/>
              <path d="M52 16l16 16" stroke="#C7D2FE" strokeWidth="1.5"/>
              <rect x="52" y="8" width="24" height="24" rx="4" fill="#EEF2FF" stroke="#C7D2FE" strokeWidth="1.5" transform="rotate(45 52 8)"/>
              <circle cx="62" cy="18" r="3" fill="#818CF8"/>
              <rect x="20" y="30" width="30" height="5" rx="2.5" fill="#C7D2FE"/>
              <rect x="20" y="40" width="22" height="4" rx="2" fill="#DDE3F8"/>
              <circle cx="72" cy="60" r="14" fill="#4F46E5"/>
              <path d="M68 60h8M72 56v8" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
            <p className="text-slate-700 font-semibold mb-1">No categories yet</p>
            <p className="text-sm text-slate-500 mb-5">Create categories to tag and score questions</p>
            {canManageCategories && (
              <Button
                onClick={openCreate}
                variant="outline"
                className="refined-action-button border-slate-300"
              >
                <Plus className="w-4 h-4 mr-2" /> Create Your First Category
              </Button>
            )}
          </div>
        ) : viewMode === "card" ? (
          /* ── Card Grid ── */
          <div className="fade-in-delay-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className="refined-category-card"
                  data-testid={`category-card-${cat.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center flex-shrink-0 border border-emerald-200">
                        <Tag className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900 truncate">{cat.name}</p>
                        {cat.description && (
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{cat.description}</p>
                        )}
                        {isSuperadmin && cat.campus_name && (
                          <p className="flex items-center gap-1 text-xs text-slate-400 mt-1 truncate">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            {cat.campus_name}
                          </p>
                        )}
                      </div>
                    </div>
                    {canManageCategories && (
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        {isOrgAdmin && campuses.length > 1 && (
                          <button
                            onClick={() => openCopyToCampus(cat)}
                            className="refined-icon-button hover:bg-indigo-50"
                            title="Copy to campus"
                            data-testid={`copy-campus-category-${cat.id}`}
                          >
                            <Copy className="w-3.5 h-3.5 text-indigo-500" />
                          </button>
                        )}
                        <button
                          onClick={() => openEdit(cat)}
                          className="refined-icon-button hover:bg-slate-100"
                          data-testid={`edit-category-${cat.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5 text-slate-500" />
                        </button>
                        <button
                          onClick={() => openDeleteWarning(cat)}
                          className="refined-icon-button hover:bg-red-50"
                          data-testid={`delete-category-${cat.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="refined-pagination flex items-center justify-between">
                <div className="text-sm text-slate-600 font-medium">
                  Page {page} of {pagination.pages} ({pagination.total} total)
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="refined-action-button border-slate-300"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                    disabled={page === pagination.pages}
                    className="refined-action-button border-slate-300"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ── List View ── */
          <div className="fade-in-delay-2">
            <div className="refined-list-table">
              {/* Header */}
              <div className={`refined-list-header ${isSuperadmin ? "with-campus" : ""}`}>
                <span>Name</span>
                <span>Description</span>
                {isSuperadmin && <span>Campus</span>}
                <span style={{ textAlign: "right" }}>Actions</span>
              </div>

              {/* Rows */}
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className={`refined-list-row ${isSuperadmin ? "with-campus" : ""}`}
                  data-testid={`category-list-row-${cat.id}`}
                >
                  {/* Name */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: 7, flexShrink: 0,
                      background: "linear-gradient(135deg, #ecfdf5, #d1fae5)",
                      border: "1px solid #a7f3d0",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Tag style={{ width: 13, height: 13, color: "#059669" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "rgb(15 23 42)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {cat.name}
                    </span>
                  </div>

                  {/* Description */}
                  <span style={{ fontSize: 12, color: "rgb(100 116 139)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {cat.description || <span style={{ color: "rgb(203 213 225)", fontStyle: "italic" }}>—</span>}
                  </span>

                  {/* Campus (superadmin) */}
                  {isSuperadmin && (
                    <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "rgb(100 116 139)", overflow: "hidden", whiteSpace: "nowrap" }}>
                      {cat.campus_name ? (
                        <>
                          <MapPin style={{ width: 12, height: 12, flexShrink: 0, color: "rgb(148 163 184)" }} />
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{cat.campus_name}</span>
                        </>
                      ) : (
                        <span style={{ color: "rgb(203 213 225)", fontStyle: "italic" }}>—</span>
                      )}
                    </span>
                  )}

                  {/* Actions */}
                  {canManageCategories && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
                      {isOrgAdmin && campuses.length > 1 && (
                        <button
                          onClick={() => openCopyToCampus(cat)}
                          className="refined-icon-button hover:bg-indigo-50"
                          title="Copy to campus"
                          data-testid={`copy-campus-category-${cat.id}`}
                        >
                          <Copy className="w-3.5 h-3.5 text-indigo-500" />
                        </button>
                      )}
                      <button
                        onClick={() => openEdit(cat)}
                        className="refined-icon-button hover:bg-slate-100"
                        data-testid={`edit-category-${cat.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5 text-slate-500" />
                      </button>
                      <button
                        onClick={() => openDeleteWarning(cat)}
                        className="refined-icon-button hover:bg-red-50"
                        data-testid={`delete-category-${cat.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {/* Footer count */}
              <div className="refined-list-footer">
                {pagination.total ?? categories.length} categor{(pagination.total ?? categories.length) !== 1 ? "ies" : "y"}
                {searchTerm.trim() && ` matching "${searchTerm}"`}
              </div>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="refined-pagination flex items-center justify-between">
                <div className="text-sm text-slate-600 font-medium">
                  Page {page} of {pagination.pages} ({pagination.total} total)
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="refined-action-button border-slate-300"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                    disabled={page === pagination.pages}
                    className="refined-action-button border-slate-300"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialog}
        onOpenChange={(open) => {
          setDialog(open);
          if (!open) setFormErrors({});
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="refined-dialog-header">
              {editing ? "Edit Category" : "Create New Category"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="refined-label">Category Name</Label>
              <Input
                value={form.name}
                onChange={(e) => {
                  setForm((p) => ({ ...p, name: e.target.value }));
                  setFormErrors((p) => ({ ...p, name: undefined }));
                }}
                data-testid="category-name-input"
                className={`refined-input ${formErrors.name ? "border-red-400" : ""}`}
                placeholder="e.g. Logical Reasoning"
              />
              {formErrors.name && <p className="text-xs text-red-500 font-medium">{formErrors.name}</p>}
            </div>
            <div className="space-y-2">
              <Label className="refined-label">Description (optional)</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                data-testid="category-desc-input"
                className="refined-input"
                placeholder="Brief description of the category"
              />
            </div>
            <Button
              onClick={handleSave}
              data-testid="save-category-btn"
              className="w-full refined-action-button bg-indigo-600 hover:bg-indigo-700 text-white h-10"
            >
              {editing ? "Update" : "Create"} Category
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Copy to Campus Dialog */}
      <Dialog open={copyCampusOpen} onOpenChange={setCopyCampusOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="refined-dialog-header">Copy Category to Campus</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {copyCategory && (
              <p className="text-sm text-slate-600">
                Copying: <span className="font-semibold">{copyCategory.name}</span>
              </p>
            )}
            <div className="space-y-2">
              <Label className="refined-label">Target Campus</Label>
              <Select value={copyTargetCampusId} onValueChange={setCopyTargetCampusId}>
                <SelectTrigger className="h-10 border-slate-300">
                  <SelectValue placeholder="Select a campus..." />
                </SelectTrigger>
                <SelectContent>
                  {campuses
                    .filter(c => c.id !== copyCategory?.campus_id)
                    .map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-slate-400" />
                          {c.name}
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyCampusOpen(false)} className="border-slate-300">Cancel</Button>
            <Button
              onClick={confirmCopyToCampus}
              disabled={!copyTargetCampusId || copyingCategory}
              className="refined-action-button bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {copyingCategory ? "Copying..." : "Copy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Warning Dialog */}
      <AlertDialog open={deleteWarningDialog} onOpenChange={setDeleteWarningDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="refined-dialog-header flex items-center gap-2">
              {categoryUsage.is_used && <AlertTriangle className="w-5 h-5 text-amber-600" />}
              Delete Category: {deletingCategory?.name}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 mt-4">
                {categoryUsage.is_used ? (
                  <>
                    <div className="refined-alert-warning">
                      <div className="text-sm font-semibold text-amber-900">
                        This category is currently used by {categoryUsage.question_count} question
                        {categoryUsage.question_count !== 1 ? "s" : ""}.
                      </div>
                    </div>
                    <div className="text-sm text-slate-600 leading-relaxed">
                      If you delete this category, it will be{" "}
                      <strong className="font-semibold text-slate-900">
                        unattached from all {categoryUsage.question_count} question
                        {categoryUsage.question_count !== 1 ? "s" : ""}
                      </strong>
                      . The questions will remain but won't have a category assigned.
                    </div>
                    <div className="text-sm text-red-600 font-medium">This action cannot be undone.</div>
                  </>
                ) : (
                  <div className="text-sm text-slate-600">
                    This category is not currently used by any questions. Are you sure you want to delete it?
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6">
            <AlertDialogCancel className="border-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="refined-alert-action">
              {categoryUsage.is_used
                ? `Delete & Unattach from ${categoryUsage.question_count} Question${
                    categoryUsage.question_count !== 1 ? "s" : ""
                  }`
                : "Delete Category"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
